<template>
  <div>

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">{{ isSelfService ? 'My Loans' : 'Loan Management' }}</h5>
        <p class="page-sub">{{ isSelfService ? 'Apply and track your loan repayments' : 'Manage loan types and employee applications' }}</p>
      </div>
      <div class="page-actions">
        <template v-if="!isSelfService">
          <button class="btn-outline" @click="activeTab = 'types'" :class="{ 'btn-active': activeTab === 'types' }">Loan Types</button>
          <button class="btn-outline" @click="activeTab = 'applications'" :class="{ 'btn-active': activeTab === 'applications' }">Applications</button>
        </template>
        <button class="btn-primary-action" @click="openApplyModal">
          <font-awesome-icon icon="plus" /> Apply for Loan
        </button>
      </div>
    </div>

    <!-- ══════════════  TAB: LOAN TYPES  ══════════════ -->
    <div v-if="activeTab === 'types' && !isSelfService">
      <div class="section-header">
        <p class="section-title">Loan Types &amp; Repayment Options</p>
        <button class="btn-primary-action" @click="openCreateType">
          <font-awesome-icon icon="plus" /> New Loan Type
        </button>
      </div>

      <div v-if="typesLoading" class="sk-table">
        <div v-for="n in 4" :key="n" class="sk-row"></div>
      </div>

      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Min Amount</th>
              <th>Max Amount</th>
              <th>Eligibility</th>
              <th>Repayment Options</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="lt in loanTypes" :key="lt.id">
              <td>
                <div class="fw-600">{{ lt.name }}</div>
                <div class="text-muted-sm">{{ lt.description }}</div>
              </td>
              <td>{{ lt.min_amount > 0 ? fmt(lt.min_amount) : '—' }}</td>
              <td>{{ lt.max_amount ? fmt(lt.max_amount) : 'No cap' }}</td>
              <td>{{ lt.eligibility_months > 0 ? lt.eligibility_months + ' month(s)' : 'None' }}</td>
              <td>
                <div class="repayment-chips">
                  <span v-for="r in lt.repayment_types" :key="r.id" class="chip" :class="r.is_active ? 'chip--blue' : 'chip--gray'">
                    {{ r.label }}
                  </span>
                  <span v-if="!lt.repayment_types?.length" class="text-muted-sm">None set</span>
                </div>
              </td>
              <td><span class="chip" :class="lt.is_active ? 'chip--green' : 'chip--gray'">{{ lt.is_active ? 'Active' : 'Inactive' }}</span></td>
              <td class="actions-cell">
                <button class="btn-icon" @click="openEditType(lt)" title="Edit"><font-awesome-icon icon="pen" /></button>
                <button class="btn-icon" @click="openRepayments(lt)" title="Manage Repayment Options"><font-awesome-icon icon="list" /></button>
                <button class="btn-icon" @click="toggleTypeStatus(lt)" title="Toggle Status"><font-awesome-icon :icon="lt.is_active ? 'toggle-on' : 'toggle-off'" /></button>
                <button class="btn-icon btn-icon--red" @click="deleteType(lt)" title="Delete"><font-awesome-icon icon="trash" /></button>
              </td>
            </tr>
            <tr v-if="!loanTypes.length">
              <td colspan="7" class="empty-cell">No loan types configured yet</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ══════════════  TAB: APPLICATIONS  ══════════════ -->
    <div v-if="activeTab === 'applications' || isSelfService">
      <div class="filter-bar">
        <select class="filter-select" v-model="reqFilters.status">
          <option value="">All Statuses</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="disbursed">Disbursed</option>
          <option value="active">Active</option>
          <option value="closed_repaid">Closed (Repaid)</option>
          <option value="closed_settled">Closed (Settled)</option>
        </select>
        <select class="filter-select" v-model="reqFilters.loan_type_id">
          <option value="">All Types</option>
          <option v-for="lt in loanTypes" :key="lt.id" :value="lt.id">{{ lt.name }}</option>
        </select>
        <input v-if="!isSelfService" type="text" class="filter-input" v-model="reqFilters.search" placeholder="Search employee…" />
        <button class="btn-primary-action" @click="applyFilters"><font-awesome-icon icon="search" /></button>
        <select class="filter-select" v-model="reqFilters.length" @change="applyFilters">
          <option :value="5">5 / page</option>
          <option :value="10">10 / page</option>
          <option :value="20">20 / page</option>
          <option :value="50">50 / page</option>
          <option :value="100">100 / page</option>
          <option :value="200">200 / page</option>
          <option :value="500">500 / page</option>
          <option :value="1000">1000 / page</option>
        </select>
        <button class="btn-export-sm" @click="openExportModal('csv')" :disabled="exportLoading"><font-awesome-icon icon="file-csv" /> CSV</button>
        <button class="btn-export-sm btn-export-sm--pdf" @click="openExportModal('pdf')" :disabled="exportLoading"><font-awesome-icon icon="file-pdf" /> PDF</button>
      </div>

      <div v-if="reqLoading" class="sk-table">
        <div v-for="n in 5" :key="n" class="sk-row"></div>
      </div>

      <div v-else class="table-card">
        <table class="data-table">
          <thead>
            <tr>
              <th v-if="!isSelfService">Employee</th>
              <th>Loan Type</th>
              <th>Amount</th>
              <th>EMI / Month</th>
              <th>Tenure</th>
              <th>Outstanding</th>
              <th>Status</th>
              <th>Applied On</th>
              <th v-if="canDisburse || canCancel"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="req in applications" :key="req.id">
              <td v-if="!isSelfService">
                <div class="emp-cell">
                  <span class="emp-name">{{ req.employee?.first_name }} {{ req.employee?.last_name }}</span>
                  <span class="emp-no">{{ req.employee?.employee_no }}</span>
                </div>
              </td>
              <td class="fw-600">{{ req.loan_type?.name }}</td>
              <td>{{ fmt(req.amount_requested) }}</td>
              <td>{{ fmt(req.emi_amount) }}</td>
              <td>{{ req.repayment_type?.label || req.installments_total + ' installment(s)' }}</td>
              <td>{{ fmt(req.outstanding_balance) }}</td>
              <td><span class="chip" :class="statusClass(req.status)">{{ statusLabel(req.status) }}</span></td>
              <td>{{ fmtDate(req.created_at) }}</td>
              <td v-if="canDisburse || canCancel" class="actions-cell">
                <button
                  v-if="canDisburse && req.status === 'approved'"
                  class="btn-disburse"
                  @click="disburse(req.id)"
                  title="Disburse loan"
                >
                  <font-awesome-icon icon="money-bill-wave" /> Disburse
                </button>
                <button
                  v-if="canCancel && ['pending_approval','approved'].includes(req.status)"
                  class="btn-cancel-leave"
                  @click="cancelLoan(req.id)"
                  title="Cancel application"
                >
                  <font-awesome-icon icon="ban" /> Cancel
                </button>
              </td>
            </tr>
            <tr v-if="!applications.length">
              <td :colspan="isSelfService ? 7 : (canDisburse || canCancel) ? 9 : 8" class="empty-cell">No loan applications found</td>
            </tr>
          </tbody>
        </table>
        <div class="pagination-bar" v-if="reqMeta.last_page > 1">
          <span class="pg-info">Page {{ reqMeta.current_page }} of {{ reqMeta.last_page }}</span>
          <div class="pg-controls">
            <button class="pg-btn" :disabled="reqMeta.current_page === 1" @click="goPage(1)"><font-awesome-icon icon="angle-double-left" /></button>
            <button class="pg-btn" :disabled="reqMeta.current_page === 1" @click="goPage(reqMeta.current_page - 1)"><font-awesome-icon icon="angle-left" /></button>
            <button v-for="p in pageRange" :key="p" class="pg-btn" :class="{ 'pg-btn--active': p === reqMeta.current_page }" @click="goPage(p)">{{ p }}</button>
            <button class="pg-btn" :disabled="reqMeta.current_page === reqMeta.last_page" @click="goPage(reqMeta.current_page + 1)"><font-awesome-icon icon="angle-right" /></button>
            <button class="pg-btn" :disabled="reqMeta.current_page === reqMeta.last_page" @click="goPage(reqMeta.last_page)"><font-awesome-icon icon="angle-double-right" /></button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══  Create / Edit Loan Type Modal  ══ -->
    <div class="modal-overlay" v-if="typeModal.open" @click.self="typeModal.open = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">{{ typeModal.id ? 'Edit Loan Type' : 'New Loan Type' }}</p>
          <button class="modal-close" @click="typeModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Name <span class="req">*</span></label>
            <input type="text" class="form-input" v-model="typeModal.name" placeholder="e.g. Salary Advance" />
          </div>
          <div class="form-row">
            <label class="form-label">Description</label>
            <textarea class="form-input" rows="2" v-model="typeModal.description" placeholder="Optional description…"></textarea>
          </div>
          <div class="form-cols">
            <div class="form-row">
              <label class="form-label">Min Amount</label>
              <input type="number" class="form-input" v-model="typeModal.min_amount" min="0" placeholder="0" />
            </div>
            <div class="form-row">
              <label class="form-label">Max Amount (leave blank for no cap)</label>
              <input type="number" class="form-input" v-model="typeModal.max_amount" min="0" placeholder="No cap" />
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">Eligibility (min months of service)</label>
            <input type="number" class="form-input" v-model="typeModal.eligibility_months" min="0" placeholder="0" />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="typeModal.open = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving" @click="saveType">
            {{ saving ? 'Saving…' : (typeModal.id ? 'Update' : 'Create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══  Repayment Options Modal  ══ -->
    <div class="modal-overlay" v-if="repayModal.open" @click.self="repayModal.open = false">
      <div class="modal-box modal-box--wide">
        <div class="modal-head">
          <p class="modal-title">Repayment Options — {{ repayModal.loanTypeName }}</p>
          <button class="modal-close" @click="repayModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <!-- Add form -->
          <div class="repay-add-row">
            <input type="text" class="form-input" v-model="repayForm.label" placeholder="Label e.g. 3 Months" style="flex:1" />
            <input type="number" class="form-input" v-model="repayForm.installments" min="1" placeholder="Installments" style="width:140px" />
            <button class="btn-primary-action" :disabled="saving" @click="addRepayment">
              <font-awesome-icon icon="plus" /> Add
            </button>
          </div>

          <!-- Repayment list -->
          <div v-if="repayLoading" class="sk-table" style="margin-top:12px">
            <div v-for="n in 3" :key="n" class="sk-row"></div>
          </div>
          <table v-else class="data-table" style="margin-top:12px">
            <thead>
              <tr>
                <th>Label</th>
                <th>Installments</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in repayModal.repayments" :key="r.id">
                <td v-if="r._editing">
                  <input type="text" class="form-input form-input--sm" v-model="r._label" />
                </td>
                <td v-else>{{ r.label }}</td>
                <td v-if="r._editing">
                  <input type="number" class="form-input form-input--sm" v-model="r._installments" min="1" style="width:90px" />
                </td>
                <td v-else>{{ r.installments }}</td>
                <td><span class="chip" :class="r.is_active ? 'chip--blue' : 'chip--gray'">{{ r.is_active ? 'Active' : 'Inactive' }}</span></td>
                <td class="actions-cell">
                  <template v-if="r._editing">
                    <button class="btn-icon" @click="saveRepayment(r)" title="Save"><font-awesome-icon icon="check" /></button>
                    <button class="btn-icon" @click="r._editing = false" title="Cancel"><font-awesome-icon icon="times" /></button>
                  </template>
                  <template v-else>
                    <button class="btn-icon" @click="editRepayment(r)" title="Edit"><font-awesome-icon icon="pen" /></button>
                    <button class="btn-icon" @click="toggleRepayStatus(r)" title="Toggle Status"><font-awesome-icon :icon="r.is_active ? 'toggle-on' : 'toggle-off'" /></button>
                    <button class="btn-icon btn-icon--red" @click="deleteRepayment(r)" title="Delete"><font-awesome-icon icon="trash" /></button>
                  </template>
                </td>
              </tr>
              <tr v-if="!repayModal.repayments.length">
                <td colspan="4" class="empty-cell">No repayment options yet. Add one above.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="modal-footer">
          <button class="btn-primary-action" @click="repayModal.open = false">Done</button>
        </div>
      </div>
    </div>

    <!-- ══  Apply for Loan Modal  ══ -->
    <div class="modal-overlay" v-if="applyModal.open" @click.self="applyModal.open = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">Apply for Loan</p>
          <button class="modal-close" @click="applyModal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div v-if="isSelfService && !employeeId" class="ss-no-profile">
            <font-awesome-icon icon="exclamation-triangle" />
            Your account is not linked to an employee profile. Contact HR.
          </div>

          <div class="form-row" v-if="!isSelfService">
            <label class="form-label">Employee <span class="req">*</span></label>
            <select class="form-input" v-model="applyModal.employee_id">
              <option value="">— select —</option>
              <option v-for="e in allEmployees" :key="e.id" :value="e.id">{{ e.first_name }} {{ e.last_name }} ({{ e.employee_no }})</option>
            </select>
          </div>

          <div class="form-row">
            <label class="form-label">Loan Type <span class="req">*</span></label>
            <select class="form-input" v-model="applyModal.loan_type_id" @change="onLoanTypeChange">
              <option value="">— select —</option>
              <option v-for="lt in activeLoanTypes" :key="lt.id" :value="lt.id">{{ lt.name }}</option>
            </select>
          </div>

          <div v-if="applyModal.selectedType" class="type-info-box">
            <span v-if="applyModal.selectedType.description">{{ applyModal.selectedType.description }}</span>
            <span v-if="applyModal.selectedType.min_amount > 0"> Min: {{ fmt(applyModal.selectedType.min_amount) }}.</span>
            <span v-if="applyModal.selectedType.max_amount"> Max: {{ fmt(applyModal.selectedType.max_amount) }}.</span>
            <span v-if="applyModal.selectedType.eligibility_months > 0"> Min service: {{ applyModal.selectedType.eligibility_months }} month(s).</span>
          </div>

          <div class="form-row">
            <label class="form-label">Repayment Tenure <span class="req">*</span></label>
            <select class="form-input" v-model="applyModal.loan_repayment_type_id" @change="calcEmi">
              <option value="">— select tenure —</option>
              <option v-for="r in applyModal.repaymentOptions" :key="r.id" :value="r.id">{{ r.label }} ({{ r.installments }} installment(s))</option>
            </select>
          </div>

          <div class="form-row">
            <label class="form-label">Amount Requested <span class="req">*</span></label>
            <input type="number" class="form-input" v-model="applyModal.amount_requested" min="1" placeholder="0.00" @input="calcEmi" />
          </div>

          <div v-if="applyModal.emi > 0" class="emi-preview">
            <font-awesome-icon icon="calculator" />
            Estimated monthly deduction: <strong>{{ fmt(applyModal.emi) }}</strong>
          </div>

          <div class="form-row">
            <label class="form-label">Notes</label>
            <textarea class="form-input" rows="2" v-model="applyModal.notes" placeholder="Optional reason or notes…"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="applyModal.open = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving || (isSelfService && !employeeId)" @click="submitLoan">
            {{ saving ? 'Submitting…' : 'Submit Application' }}
          </button>
        </div>
      </div>
    </div>

  <!-- ── Export Column Picker ── -->
  <transition name="fade">
    <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
      <div class="col-picker-modal">
        <div class="col-picker-header">
          <div>
            <h3 class="col-picker-title">Select Columns</h3>
            <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
          </div>
          <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="col-picker-actions">
          <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
          <span class="col-picker-sep">·</span>
          <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
          <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
        </div>
        <div class="col-picker-grid">
          <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
            <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
            <span class="col-picker-label">{{ col.label }}</span>
          </label>
        </div>
        <div class="col-picker-footer">
          <button class="col-picker-btn-ghost" @click="showExportModal = false">Cancel</button>
          <button class="col-picker-btn-primary" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
            <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
            Export {{ pendingExportFmt.toUpperCase() }}
          </button>
        </div>
      </div>
    </div>
  </transition>

  </div>
</template>

<script>
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default {
  name: 'LoansPage',
  data() {
    return {
      activeTab: 'applications',
      saving: false,
      exportLoading: false,
      showExportModal: false,
      pendingExportFmt: 'csv',
      selectedExportCols: [],
      exportColumnDefs: [
        { key: 'employee',    label: 'Employee',    pdfWidth: 110 },
        { key: 'loan_type',   label: 'Loan Type',   pdfWidth: 100 },
        { key: 'amount',      label: 'Amount',      pdfWidth: 85 },
        { key: 'emi',         label: 'EMI/Month',   pdfWidth: 85 },
        { key: 'tenure',      label: 'Tenure',      pdfWidth: 95 },
        { key: 'outstanding', label: 'Outstanding', pdfWidth: 90 },
        { key: 'status',      label: 'Status',      pdfWidth: 85 },
        { key: 'applied_on',  label: 'Applied On',  pdfWidth: 96 },
      ],

      // Loan types
      loanTypes: [],
      typesLoading: false,
      typeModal: { open: false, id: null, name: '', description: '', min_amount: 0, max_amount: '', eligibility_months: 0 },

      // Repayment options modal
      repayModal: { open: false, loanTypeId: null, loanTypeName: '', repayments: [] },
      repayLoading: false,
      repayForm: { label: '', installments: '' },

      // Applications
      applications: [],
      reqLoading: false,
      reqMeta: { current_page: 1, last_page: 1 },
      reqFilters: { status: '', loan_type_id: '', search: '', employee_id: '', length: 5, page: 1 },

      // Apply modal
      applyModal: {
        open: false, employee_id: '', loan_type_id: '', loan_repayment_type_id: '',
        amount_requested: '', notes: '', selectedType: null, repaymentOptions: [], emi: 0,
      },

      allEmployees: [],
    };
  },

  computed: {
    isSelfService() {
      return this.$route?.query?.context === 'self-service';
    },
    employeeId() {
      return this.$store?.state?.getLoggedInUser?.employee_id || null;
    },
    canDisburse() {
      const p = this.$store?.state?.getCurrentUserPermission;
      return !this.isSelfService && !!(p?.hr_module || p?.administration_module);
    },
    canCancel() {
      const p = this.$store?.state?.getCurrentUserPermission;
      return !this.isSelfService && !!(p?.hr_module || p?.administration_module);
    },
    activeLoanTypes() {
      return this.loanTypes.filter(lt => lt.is_active);
    },
    pageRange() {
      if (!this.reqMeta || !this.reqMeta.last_page) return [];
      const cur = this.reqMeta.current_page, last = this.reqMeta.last_page;
      const range = [];
      for (let i = Math.max(1, cur - 2); i <= Math.min(last, cur + 2); i++) range.push(i);
      return range;
    },
  },

  watch: {
    employeeId(val) {
      if (this.isSelfService && val) {
        this.reqFilters.employee_id = val;
        this.fetchApplications();
      }
    },
  },

  mounted() {
    this.fetchLoanTypes();
    if (this.isSelfService && this.employeeId) {
      this.reqFilters.employee_id = this.employeeId;
    }
    this.fetchApplications();
    if (!this.isSelfService) this.fetchEmployees();
  },

  methods: {
    // ── Loan Types ─────────────────────────────────────────────────────────
    async fetchLoanTypes() {
      this.typesLoading = true;
      try {
        const { data } = await axios.get('/hr/loan-type');
        this.loanTypes = data.data || [];
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load loan types');
      } finally {
        this.typesLoading = false;
      }
    },
    openCreateType() {
      this.typeModal = { open: true, id: null, name: '', description: '', min_amount: 0, max_amount: '', eligibility_months: 0 };
    },
    openEditType(lt) {
      this.typeModal = { open: true, id: lt.id, name: lt.name, description: lt.description || '', min_amount: lt.min_amount, max_amount: lt.max_amount || '', eligibility_months: lt.eligibility_months };
    },
    async saveType() {
      if (!this.typeModal.name) { this.$toast?.error('Name is required'); return; }
      this.saving = true;
      try {
        if (this.typeModal.id) {
          await axios.put(`/hr/loan-type/${this.typeModal.id}`, this.typeModal);
        } else {
          await axios.post('/hr/loan-type', this.typeModal);
        }
        this.typeModal.open = false;
        this.$toast?.success('Loan type saved');
        this.fetchLoanTypes();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Save failed');
      } finally {
        this.saving = false;
      }
    },
    async toggleTypeStatus(lt) {
      try {
        await axios.put(`/hr/loan-type/${lt.id}/status`);
        lt.is_active = !lt.is_active;
        this.$toast?.success('Status updated');
      } catch {
        this.$toast?.error('Failed to update status');
      }
    },
    async deleteType(lt) {
      if (!confirm(`Delete loan type "${lt.name}"?`)) return;
      try {
        await axios.delete(`/hr/loan-type/${lt.id}`);
        this.$toast?.success('Deleted');
        this.fetchLoanTypes();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Delete failed');
      }
    },

    // ── Repayment Options ───────────────────────────────────────────────────
    async openRepayments(lt) {
      this.repayModal = { open: true, loanTypeId: lt.id, loanTypeName: lt.name, repayments: [] };
      this.repayForm = { label: '', installments: '' };
      this.repayLoading = true;
      try {
        const { data } = await axios.get(`/hr/loan-type/${lt.id}/repayments`);
        this.repayModal.repayments = (data.data || []).map(r => ({ ...r, _editing: false, _label: r.label, _installments: r.installments }));
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load repayment options');
      } finally {
        this.repayLoading = false;
      }
    },
    async addRepayment() {
      if (!this.repayForm.label || !this.repayForm.installments) {
        this.$toast?.error('Label and installments are required');
        return;
      }
      this.saving = true;
      try {
        const { data } = await axios.post(`/hr/loan-type/${this.repayModal.loanTypeId}/repayments`, this.repayForm);
        const r = data.data;
        this.repayModal.repayments.push({ ...r, _editing: false, _label: r.label, _installments: r.installments });
        this.repayForm = { label: '', installments: '' };
        this.$toast?.success('Repayment option added');
        this.fetchLoanTypes();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to add');
      } finally {
        this.saving = false;
      }
    },
    editRepayment(r) {
      r._label = r.label;
      r._installments = r.installments;
      r._editing = true;
    },
    async saveRepayment(r) {
      try {
        await axios.put(`/hr/loan-type/repayments/${r.id}`, { label: r._label, installments: r._installments });
        r.label = r._label;
        r.installments = r._installments;
        r._editing = false;
        this.$toast?.success('Updated');
        this.fetchLoanTypes();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to update');
      }
    },
    async toggleRepayStatus(r) {
      try {
        await axios.put(`/hr/loan-type/repayments/${r.id}/status`);
        r.is_active = !r.is_active;
        this.$toast?.success('Status updated');
        this.fetchLoanTypes();
      } catch {
        this.$toast?.error('Failed');
      }
    },
    async deleteRepayment(r) {
      if (!confirm(`Delete "${r.label}"?`)) return;
      try {
        await axios.delete(`/hr/loan-type/repayments/${r.id}`);
        this.repayModal.repayments = this.repayModal.repayments.filter(x => x.id !== r.id);
        this.$toast?.success('Deleted');
        this.fetchLoanTypes();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Delete failed');
      }
    },

    // ── Applications ────────────────────────────────────────────────────────
    applyFilters() {
      this.reqFilters.page = 1;
      this.fetchApplications();
    },
    async fetchApplications() {
      if (this.isSelfService && this.employeeId) {
        this.reqFilters.employee_id = this.employeeId;
      }
      this.reqLoading = true;
      try {
        const { data } = await axios.get('/hr/loan', { params: this.reqFilters });
        this.applications = data.data?.data || [];
        this.reqMeta = { current_page: data.data?.current_page || 1, last_page: data.data?.last_page || 1 };
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Failed to load applications');
      } finally {
        this.reqLoading = false;
      }
    },
    goPage(p) {
      this.reqFilters.page = p;
      this.fetchApplications();
    },
    async disburse(id) {
      if (!confirm('Mark this loan as disbursed?')) return;
      try {
        await axios.put(`/hr/loan/${id}/disburse`);
        this.$toast?.success('Loan disbursed');
        this.fetchApplications();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Disbursement failed');
      }
    },
    async cancelLoan(id) {
      if (!confirm('Cancel this loan application?')) return;
      try {
        await axios.put(`/hr/loan/${id}/cancel`);
        this.$toast?.success('Application cancelled');
        this.fetchApplications();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Cancel failed');
      }
    },

    // ── Apply Modal ─────────────────────────────────────────────────────────
    openApplyModal() {
      const empId = this.isSelfService ? (this.employeeId || '') : '';
      this.applyModal = {
        open: true, employee_id: empId, loan_type_id: '', loan_repayment_type_id: '',
        amount_requested: '', notes: '', selectedType: null, repaymentOptions: [], emi: 0,
      };
    },
    onLoanTypeChange() {
      const selected = this.loanTypes.find(lt => lt.id === this.applyModal.loan_type_id);
      this.applyModal.selectedType = selected || null;
      this.applyModal.repaymentOptions = (selected?.repayment_types || []).filter(r => r.is_active);
      this.applyModal.loan_repayment_type_id = '';
      this.applyModal.emi = 0;
    },
    calcEmi() {
      const amount = parseFloat(this.applyModal.amount_requested) || 0;
      const repayment = this.applyModal.repaymentOptions.find(r => r.id === this.applyModal.loan_repayment_type_id);
      const installments = repayment?.installments || 0;
      this.applyModal.emi = installments > 0 ? Math.round((amount / installments) * 100) / 100 : 0;
    },
    async submitLoan() {
      if (this.isSelfService && !this.applyModal.employee_id) {
        this.$toast?.error('Your account is not linked to an employee profile. Contact HR.'); return;
      }
      if (!this.applyModal.employee_id || !this.applyModal.loan_type_id || !this.applyModal.loan_repayment_type_id || !this.applyModal.amount_requested) {
        this.$toast?.error('Employee, loan type, tenure, and amount are required'); return;
      }
      this.saving = true;
      try {
        await axios.post('/hr/loan', {
          employee_id:            this.applyModal.employee_id,
          loan_type_id:           this.applyModal.loan_type_id,
          loan_repayment_type_id: this.applyModal.loan_repayment_type_id,
          amount_requested:       this.applyModal.amount_requested,
          notes:                  this.applyModal.notes,
        });
        this.applyModal.open = false;
        this.$toast?.success('Loan application submitted');
        this.fetchApplications();
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Submission failed');
      } finally {
        this.saving = false;
      }
    },

    // ── Export ──────────────────────────────────────────────────────────────
    openExportModal(format) {
      this.pendingExportFmt   = format;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },

    getLoanValue(row, key) {
      switch (key) {
        case 'employee':    return row.employee ? `${row.employee.first_name} ${row.employee.last_name}` : '—';
        case 'loan_type':   return row.loan_type?.name || '—';
        case 'amount':      return this.fmt(row.amount_requested);
        case 'emi':         return this.fmt(row.emi_amount);
        case 'tenure':      return row.repayment_type?.label || (row.installments_total ? row.installments_total + ' instalment(s)' : '—');
        case 'outstanding': return this.fmt(row.outstanding_balance);
        case 'status':      return this.statusLabel(row.status);
        case 'applied_on':  return this.fmtDate(row.created_at);
        default:            return '—';
      }
    },

    async doExport() {
      if (!this.selectedExportCols.length) return;
      this.exportLoading   = true;
      this.showExportModal = false;
      try {
        const params = { ...this.reqFilters, page: 1, length: 10000 };
        const { data } = await axios.get('/hr/loan', { params });
        const rows    = data.data?.data || data.data || [];
        const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
        const headers = cols.map(c => c.label);
        const body    = rows.map(r => cols.map(c => this.getLoanValue(r, c.key)));
        const date    = new Date().toISOString().slice(0, 10);

        if (this.pendingExportFmt === 'csv') {
          const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = `loans-${date}.csv`; a.click();
          URL.revokeObjectURL(url);
        } else {
          const doc      = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
          const usable   = 761;
          const scale    = usable / cols.reduce((s, c) => s + c.pdfWidth, 0);
          const colStyles = {};
          cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
          doc.setFontSize(18); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69);
          doc.text('Loan Applications', 40, 46);
          doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176);
          doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
          doc.autoTable({
            startY: 78, head: [headers], body,
            styles: { fontSize:7.5, cellPadding:3.5, textColor:[26,43,69], overflow:'ellipsize' },
            headStyles: { fillColor:[0,74,173], textColor:255, fontStyle:'bold', fontSize:7.5 },
            alternateRowStyles: { fillColor:[248,250,252] },
            columnStyles: colStyles, tableWidth: usable, margin: { left:40, right:40 },
            didDrawPage(d) {
              doc.setFontSize(8); doc.setTextColor(192,202,214);
              doc.text(`Page ${d.pageNumber}`, doc.internal.pageSize.width/2, doc.internal.pageSize.height-16, { align:'center' });
            },
          });
          doc.save(`loans-${date}.pdf`);
        }
      } catch {
        this.$toast?.error('Export failed. Please try again.');
      } finally {
        this.exportLoading = false;
      }
    },

    // ── Employees ───────────────────────────────────────────────────────────
    async fetchEmployees() {
      try {
        const { data } = await axios.get('/payroll/employees/active');
        this.allEmployees = data.data || [];
      } catch { /* silent */ }
    },

    // ── Helpers ─────────────────────────────────────────────────────────────
    fmt(v) {
      return v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
    },
    fmtDate(d) {
      return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
    },
    statusLabel(s) {
      return { pending_approval: 'Pending', approved: 'Approved', rejected: 'Rejected', disbursed: 'Disbursed', active: 'Active', closed_repaid: 'Repaid', closed_settled: 'Settled' }[s] || s;
    },
    statusClass(s) {
      return { pending_approval: 'chip--amber', approved: 'chip--blue', rejected: 'chip--red', disbursed: 'chip--purple', active: 'chip--green', closed_repaid: 'chip--gray', closed_settled: 'chip--gray' }[s] || 'chip--gray';
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }

.section-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.section-title  { font-size:14px; font-weight:700; color:#1a2b45; margin:0; }

.filter-bar { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.filter-input,.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline  { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-outline:hover,.btn-active { background:#f0f7ff; border-color:#004AAD; color:#004AAD; }
.btn-icon     { background:none; border:none; cursor:pointer; color:#7a90b0; padding:5px 8px; border-radius:6px; font-size:13px; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red:hover { color:#dc2626; }

.btn-disburse   { display:inline-flex; align-items:center; gap:5px; background:#dcfce7; color:#16a34a; border:1px solid #bbf7d0; border-radius:6px; padding:4px 10px; font-size:12px; font-weight:600; cursor:pointer; }
.btn-disburse:hover { background:#bbf7d0; }
.btn-cancel-leave { display:inline-flex; align-items:center; gap:5px; background:#fff0f0; color:#dc2626; border:1px solid #fecaca; border-radius:6px; padding:4px 10px; font-size:12px; font-weight:600; cursor:pointer; }
.btn-cancel-leave:hover { background:#fee2e2; }

.table-card  { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table  { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; color:#1a2b45; vertical-align:middle; }
.empty-cell  { text-align:center; color:#aab8cc; padding:40px !important; }
.actions-cell { white-space:nowrap; }
.fw-600 { font-weight:600; }
.text-muted-sm { font-size:11px; color:#7a90b0; margin-top:2px; }

.repayment-chips { display:flex; flex-wrap:wrap; gap:4px; }

.emp-cell { display:flex; flex-direction:column; gap:1px; }
.emp-name { font-weight:600; font-size:13px; color:#1a2b45; }
.emp-no   { font-size:11px; color:#7a90b0; }

.chip { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--amber  { background:#fef9c3; color:#ca8a04; }
.chip--red    { background:#fee2e2; color:#dc2626; }
.chip--blue   { background:#dbeafe; color:#1d4ed8; }
.chip--purple { background:#ede9fe; color:#7c3aed; }
.chip--gray   { background:#f1f5f9; color:#64748b; }

.pagination-bar { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; padding:12px 16px; border-top:1px solid #f0f4f8; }
.pg-info     { font-size:13px; color:#7a90b0; }
.pg-controls { display:flex; gap:4px; }
.pg-btn      { width:32px; height:32px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; color:#1a2b45; font-size:13px; font-weight:500; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
.pg-btn:hover:not(:disabled) { background:#f0f4fb; border-color:#c5d6f5; color:#004AAD; }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-btn--active { background:#004AAD; border-color:#004AAD; color:#fff; font-weight:700; }
.pg-btn--active:hover { background:#004AAD; color:#fff; }

/* Skeleton */
.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:48px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Modals */
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--wide { max-width:680px; }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title   { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close   { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body    { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer  { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }
.form-cols     { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.form-row      { display:flex; flex-direction:column; gap:5px; }
.form-label    { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input    { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-input:focus { border-color:#004AAD; }
.form-input--sm { padding:5px 8px; font-size:12px; }
.req { color:#dc2626; }

.repay-add-row { display:flex; gap:8px; align-items:center; }

.type-info-box { background:#f0f7ff; border:1px solid #bfdbfe; border-radius:8px; padding:8px 12px; font-size:12px; color:#1e40af; }

.emi-preview { background:#f0fdf4; border:1px solid #bbf7d0; border-radius:8px; padding:10px 14px; font-size:13px; color:#15803d; display:flex; align-items:center; gap:8px; }

.ss-no-profile { display:flex; align-items:center; gap:8px; background:#fef9c3; border:1px solid #fde047; border-radius:8px; padding:10px 14px; font-size:13px; color:#854d0e; font-weight:500; }

.btn-export-sm { display:inline-flex; align-items:center; gap:5px; background:#fff; color:#16a34a; border:1px solid #bbf7d0; font-size:12px; font-weight:600; padding:0 12px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; }
.btn-export-sm:hover:not(:disabled) { background:#f0fdf4; }
.btn-export-sm--pdf { color:#dc2626; border-color:#fecaca; }
.btn-export-sm--pdf:hover:not(:disabled) { background:#fff5f5; }
.btn-export-sm:disabled { opacity:.45; cursor:not-allowed; }

/* -- Column picker modal ------------------------------------------------- */
.col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
.col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
.col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
.col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
.col-picker-close:hover { color:#1a2b45; }
.col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; font-size:12px; border-bottom:1px solid #f0f4f9; }
.col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
.col-picker-link:hover { text-decoration:underline; }
.col-picker-sep      { color:#c5d3e0; }
.col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
.col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
.col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
.col-picker-item:hover { background:#f4f7fb; }
.col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
.col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
.col-picker-btn-ghost   { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; font-size:13px; font-weight:600; padding:8px 14px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; font-family:inherit; }
.col-picker-btn-ghost:hover { background:#e4eaf2; }
.col-picker-btn-primary { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; transition:background .15s; }
.col-picker-btn-primary:hover:not(:disabled) { background:#003b8e; }
.col-picker-btn-primary:disabled { opacity:.45; cursor:not-allowed; }
.fade-enter-active, .fade-leave-active { transition:opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity:0; }
</style>
